/* eslint-disable no-unused-vars */
import { useState, useEffect } from "react";
import * as yup from "yup";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { Grid, FormControl, makeStyles, Theme, Button, Link } from "@material-ui/core";
import { useDynamicYupValidations } from "../../../../libs/yup-validations";
import CustomTextField from "../../../forms/CustomTextField";

interface TwoFaCodeProps {
  onSubmit: (code: string) => void;
  onCancel: () => void;
  onResend: () => void;
}

const TwoFaCode: React.FC<TwoFaCodeProps> = ({ onSubmit, onCancel, onResend }) => {
  const classes = useStyles();
  const { t } = useTranslation(["authentication", "common", "snackbars"]);
  const { YupValidation2FaCode } = useDynamicYupValidations();

  const [resendCooldown, setResendCooldown] = useState(0);

  const formInitialvalues = {
    user_id: "",
    code: "",
  };

  const validationSchema = yup.object({
    code: YupValidation2FaCode(),
  });

  useEffect(() => {
    if (resendCooldown === 0) return;

    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [resendCooldown]);

  const handleResendClick = () => {
    if (resendCooldown === 0) {
      onResend();
      setResendCooldown(60);
    }
  };

  return (
    <>
      <Formik
        onSubmit={({ code }) => onSubmit(code)}
        initialValues={formInitialvalues}
        validationSchema={validationSchema}
      >
        <Form className={classes.form} data-testid="auth-2fa-form">
          <Grid container>
            <Grid item xs={12} sm={6}>
              <Grid container direction="column" spacing={2}>
                <Grid item>
                  <FormControl fullWidth>
                    <CustomTextField
                      autoComplete="one-time-code"
                      name="code"
                      id="2fa-code"
                      label="2FA Code"
                      type="text"
                      placeholder="- - - - - -"
                      required
                      inputProps={{
                        maxLength: 6,
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                    />
                  </FormControl>
                </Grid>

                <Grid item>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <Button
                          id="submit-2fa-code"
                          type="submit"
                          size="large"
                          variant="contained"
                          color="primary"
                          disableElevation
                          disableRipple
                          fullWidth
                        >
                          {t("auth2fa.activate", {
                            ns: "authentication",
                          })}
                        </Button>
                      </FormControl>
                    </Grid>

                    <Grid item xs={6}>
                      <Button
                        id="cancel-2fa-code"
                        type="button"
                        size="large"
                        variant="outlined"
                        color="primary"
                        disableElevation
                        disableRipple
                        fullWidth
                        onClick={onCancel}
                      >
                        {t("forms.buttons.cancel", {
                          ns: "common",
                        })}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Form>
      </Formik>

      <Grid item xs={12} sm={6} className={classes.resend}>
        <Link
          className="icon-arrow-right large-link"
          component="button"
          onClick={handleResendClick}
          disabled={resendCooldown > 0}
          style={{
            pointerEvents: resendCooldown > 0 ? "none" : "auto",
            opacity: resendCooldown > 0 ? 0.5 : 1,
          }}
        >
          {resendCooldown > 0
            ? t("auth2fa.resendWait", { count: resendCooldown, ns: "authentication" })
            : t("auth2fa.resendCode", { ns: "authentication" })}
        </Link>
      </Grid>
    </>
  );
};

export default TwoFaCode;

const useStyles = makeStyles((theme: Theme) => ({
  grid: {
    marginTop: theme.spacing(6),
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
  form: {
    marginTop: theme.spacing(2),
    width: "100%",
    "& > *": {
      marginBottom: theme.spacing(1),
    },
  },
  text: {
    marginBottom: theme.spacing(1),
  },
  resend: {
    marginTop: theme.spacing(2),
  },
}));
